import { queryOptions, useMutation, useQuery } from '@tanstack/react-query';

import type { MutationConfig, QueryConfig } from '@/lib/react-query';
import { env } from '@/constants/env';

export type StartImage2VideoPayload = {
  images: (Blob | string)[];
  durationSec: number;
  quality?: string;
  cropToAspectRatio?: string | null;
};

export type StartImage2VideoResponse = {
  task_id: string;
  status: string;
  queue_position?: number;
};

export type ProcessingResponse = {
  task_id: string;
  status: 'processing' | string;
  imagename?: string;
  mode?: string;
  retry_count?: number;
  processing_time_seconds?: number;
  gpu_id?: string | null;
  queue_position?: number;
  [key: string]: unknown;
};

export type Image2VideoResult =
  | { kind: 'processing'; data: ProcessingResponse }
  | { kind: 'done'; blob: Blob; contentType: string };

const aiKeys = {
  image2video: {
    start: ['ai', 'image2video', 'start'] as const,
    result: (taskId: string | null | undefined) => ['ai', 'image2video', 'result', taskId] as const,
  },
} as const;

const extractErrorMessage = async (response: Response, defaultMessage: string): Promise<string> => {
  let message = defaultMessage;

  try {
    const text = await response.text();
    if (!text) return message;

    try {
      const data = JSON.parse(text) as { message?: unknown; detail?: unknown };
      if (typeof data?.message === 'string') {
        message = data.message;
      } else if (typeof data?.detail === 'string') {
        message = data.detail;
      } else {
        message = text;
      }
    } catch {
      message = text;
    }
  } catch {
    // ignore and keep defaultMessage
  }

  return message;
};

const resolveImageBlob = async (
  image: Blob | string
): Promise<{ blob: Blob; filename?: string }> => {
  if (image instanceof Blob) {
    const fileLike = image as Blob & { name?: string };
    const filename =
      typeof fileLike.name === 'string' && fileLike.name.length > 0 ? fileLike.name : undefined;

    return { blob: image, filename };
  }

  const response = await fetch(image);
  if (!response.ok) {
    throw new Error(`Failed to fetch image (${response.status})`);
  }

  const blob = await response.blob();

  const filename = (() => {
    try {
      const parsed = new URL(image);
      const last = parsed.pathname.split('/').filter(Boolean).pop();
      return last?.includes('.') ? last : undefined;
    } catch {
      return undefined;
    }
  })();

  return { blob, filename };
};

export const startImage2Video = async (
  payload: StartImage2VideoPayload
): Promise<StartImage2VideoResponse> => {
  const blobs = await Promise.all(payload.images.map((image) => resolveImageBlob(image)));

  const form = new FormData();
  blobs.forEach(({ blob, filename }) => {
    form.append('image', blob, filename ?? 'image.jpg');
  });
  const durationSeconds = Math.round(payload.durationSec);
  form.append('duration', String(durationSeconds));
  form.append('quality', payload.quality ?? '480p');
  if (payload.cropToAspectRatio) {
    form.append('crop_to_aspect_ratio', payload.cropToAspectRatio);
  }

  const response = await fetch(`${env.API_URL}/image2video`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });

  if (!response.ok) {
    const message = await extractErrorMessage(
      response,
      `Failed to start image2video (${response.status})`
    );

    throw new Error(message);
  }

  return response.json();
};

export const getImage2VideoResult = async (taskId: string): Promise<Image2VideoResult> => {
  const response = await fetch(`${env.API_URL}/image2video/result/${taskId}`, {
    method: 'GET',
    credentials: 'include',
  });

  if (response.status === 202) {
    const data = (await response.json()) as ProcessingResponse;
    return { kind: 'processing', data };
  }

  if (response.status === 200) {
    const contentType = response.headers.get('content-type') ?? 'video/mp4';
    const rawBlob = await response.blob();
    const blob = new Blob([rawBlob], { type: contentType });

    return { kind: 'done', blob, contentType };
  }

  let message = `Failed to get image2video result (${response.status})`;

  message = await extractErrorMessage(response, message);

  throw new Error(message);
};

type StartImage2VideoOptions = {
  mutationConfig?: MutationConfig<typeof startImage2Video>;
};

type Image2VideoResultOptions = {
  taskId?: string | null;
  queryConfig?: QueryConfig<typeof getImage2VideoResult>;
};

export const getStartImage2VideoMutationOptions = ({
  mutationConfig,
}: StartImage2VideoOptions = {}) => ({
  mutationKey: aiKeys.image2video.start,
  mutationFn: startImage2Video,
  ...mutationConfig,
});

export const useStartImage2Video = (options: StartImage2VideoOptions = {}) => {
  return useMutation(getStartImage2VideoMutationOptions(options));
};

export const getImage2VideoResultQueryOptions = (taskId: string) =>
  queryOptions({
    queryKey: aiKeys.image2video.result(taskId),
    queryFn: () => getImage2VideoResult(taskId),
    staleTime: 0,
  });

export const useImage2VideoResult = ({ taskId, queryConfig }: Image2VideoResultOptions) => {
  const enabled = !!taskId;

  const baseOptions = taskId
    ? getImage2VideoResultQueryOptions(taskId)
    : {
        queryKey: aiKeys.image2video.result(null),
        queryFn: async (): Promise<Image2VideoResult> => ({
          kind: 'processing',
          data: {
            task_id: '',
            status: 'processing',
          },
        }),
      };

  return useQuery({
    ...baseOptions,
    enabled,
    refetchInterval: (query) => {
      if (!enabled) return false;

      const data = query.state.data;
      if (!data) return 5000;

      return data.kind === 'processing' ? 5000 : false;
    },
    ...queryConfig,
  });
};
